<div class="relative w-full px-8 md:px-12">
    <!-- Navigation Arrows -->
    <div class="mb-4 hidden justify-end gap-2 sm:flex">
        @if (hasPrevious()) {
            <button
                mat-icon-button
                class="rounded-full bg-white shadow-lg transition-all hover:scale-110"
                (click)="previous()"
                [attr.aria-label]="'SHARED.CAROUSEL.PREVIOUS' | translate">
                <mat-icon class="text-shrl-blue-500">chevron_left</mat-icon>
            </button>
        }
        @if (hasNext()) {
            <button
                mat-icon-button
                class="rounded-full bg-white shadow-lg transition-all hover:scale-110"
                (click)="next()"
                [attr.aria-label]="'SHARED.CAROUSEL.NEXT' | translate">
                <mat-icon class="text-shrl-blue-500">chevron_right</mat-icon>
            </button>
        }
    </div>

    <!-- Carousel Container -->
    <div
        class="overflow-hidden"
        style="touch-action: pan-y pinch-zoom"
        #carouselContainer
        shrlSwipe
        (swipeLeft)="onSwipeLeft()"
        (swipeRight)="onSwipeRight()">
        <div
            class="flex gap-4 transition-transform duration-300 ease-in-out"
            [style.transform]="'translateX(-' + transformOffset() + 'px)'">
            @for (item of items(); track $index) {
                <div
                    class="relative flex-shrink-0 basis-full sm:basis-[calc(50%-0.5rem)] lg:basis-[calc(25%-0.75rem)]">
                    <div class="group relative h-full w-full">
                        <!-- Button Above Image -->
                        @if (item.linkUrl && item.buttonPosition === 'above') {
                            <div class="mb-2 flex justify-center">
                                @if (item.linkIcon) {
                                    @if (isExternalUrl(item.linkUrl)) {
                                        <a
                                            [href]="item.linkUrl"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            mat-icon-button
                                            class="rounded-full bg-white shadow-md transition-all hover:scale-110">
                                            <mat-icon>{{
                                                item.linkIcon
                                            }}</mat-icon>
                                        </a>
                                    } @else {
                                        <a
                                            [routerLink]="item.linkUrl"
                                            mat-icon-button
                                            class="rounded-full bg-white shadow-md transition-all hover:scale-110">
                                            <mat-icon>{{
                                                item.linkIcon
                                            }}</mat-icon>
                                        </a>
                                    }
                                } @else {
                                    @if (isExternalUrl(item.linkUrl)) {
                                        <a
                                            [href]="item.linkUrl"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            mat-stroked-button
                                            class="bg-white">
                                            {{
                                                getTranslatedValue(
                                                    item.linkLabel
                                                ) ||
                                                    ('SHARED.CAROUSEL.VIEW'
                                                        | translate)
                                            }}
                                        </a>
                                    } @else {
                                        <a
                                            [routerLink]="item.linkUrl"
                                            mat-stroked-button
                                            class="bg-white">
                                            {{
                                                getTranslatedValue(
                                                    item.linkLabel
                                                ) ||
                                                    ('SHARED.CAROUSEL.VIEW'
                                                        | translate)
                                            }}
                                        </a>
                                    }
                                }
                            </div>
                        }

                        <!-- Image Container -->
                        <div class="relative overflow-hidden rounded-lg">
                            <img
                                [src]="item.imageSrc"
                                [alt]="getTranslatedValue(item.imageAlt)"
                                class="h-64 w-full object-cover transition-transform duration-300 group-hover:scale-110 md:h-80" />

                            <!-- Hover Description Overlay -->
                            @if (item.description) {
                                <div
                                    class="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 via-black/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                    <p
                                        class="p-4 text-sm text-white md:text-base">
                                        {{
                                            getTranslatedValue(item.description)
                                        }}
                                    </p>
                                </div>
                            }
                        </div>

                        <!-- Button Below Image -->
                        @if (item.linkUrl && item.buttonPosition !== 'above') {
                            <div class="mt-2 flex justify-center">
                                @if (item.linkIcon) {
                                    @if (isExternalUrl(item.linkUrl)) {
                                        <a
                                            [href]="item.linkUrl"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            mat-icon-button
                                            class="rounded-full bg-white shadow-md transition-all hover:scale-110">
                                            <mat-icon>{{
                                                item.linkIcon
                                            }}</mat-icon>
                                        </a>
                                    } @else {
                                        <a
                                            [routerLink]="item.linkUrl"
                                            mat-icon-button
                                            class="rounded-full bg-white shadow-md transition-all hover:scale-110">
                                            <mat-icon>{{
                                                item.linkIcon
                                            }}</mat-icon>
                                        </a>
                                    }
                                } @else {
                                    @if (isExternalUrl(item.linkUrl)) {
                                        <a
                                            [href]="item.linkUrl"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            mat-stroked-button
                                            class="bg-white">
                                            {{
                                                getTranslatedValue(
                                                    item.linkLabel
                                                ) ||
                                                    ('SHARED.CAROUSEL.VIEW'
                                                        | translate)
                                            }}
                                        </a>
                                    } @else {
                                        <a
                                            [routerLink]="item.linkUrl"
                                            mat-stroked-button
                                            class="bg-white">
                                            {{
                                                getTranslatedValue(
                                                    item.linkLabel
                                                ) ||
                                                    ('SHARED.CAROUSEL.VIEW'
                                                        | translate)
                                            }}
                                        </a>
                                    }
                                }
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    </div>

    <!-- Dots Indicator (Optional) -->
    @if (dotsCount().length > 0) {
        <div class="mt-4 flex justify-center gap-2">
            @for (dotIndex of dotsCount(); track dotIndex) {
                <button
                    class="h-2 w-2 rounded-full transition-all"
                    [class.bg-shrl-blue-500]="isDotActive(dotIndex)"
                    [class.bg-gray-300]="!isDotActive(dotIndex)"
                    (click)="goToPage(dotIndex)"
                    [attr.aria-label]="
                        ('SHARED.CAROUSEL.GO_TO_SLIDE' | translate) +
                        ' ' +
                        (dotIndex + 1)
                    "></button>
            }
        </div>
    }
</div>
